import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Save, Plus, Edit, Trash2, X, Search, Filter, Globe, Sun, Moon } from 'lucide-react';
import { 
  createSetting, 
  getSettingsByType, 
  updateSetting, 
  deleteSetting 
} from '../services/api';
import Toast from '../components/Toast';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';

const Settings = () => {
  const { t, language, changeLanguage } = useLanguage();
  const { theme, setTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('todayRates');

  // Loan Settings State
  const [loanSettings, setLoanSettings] = useState([]);
  const [loadingLoan, setLoadingLoan] = useState(false);
  const [showLoanCreateModal, setShowLoanCreateModal] = useState(false);
  const [showLoanEditModal, setShowLoanEditModal] = useState(false);
  const [showLoanDeleteModal, setShowLoanDeleteModal] = useState(false);
  const [loanSettingToDelete, setLoanSettingToDelete] = useState(null);
  const [editingLoanSetting, setEditingLoanSetting] = useState(null);
  const [loanFilter, setLoanFilter] = useState('all');
  const [loanSearchTerm, setLoanSearchTerm] = useState('');
  const [loanCreateForm, setLoanCreateForm] = useState({
    name: 'Gold',
    interestRate: '',
    amountMin: '',
    amountMax: ''
  });
  const [loanEditForm, setLoanEditForm] = useState({
    interestRate: '',
    amountMin: '',
    amountMax: ''
  });

  const [notificationTemplates, setNotificationTemplates] = useState({
    loanExpiryReminder: 'Dear {customer_name}, your loan {loan_id} is due on {due_date}. Please make the payment to avoid penalties. Thank you, {business_name}.',
    paymentConfirmation: 'Dear {customer_name}, we have received a payment of {amount} for your loan {loan_id}. Your new outstanding balance is {balance}. Thank you, {business_name}.'
  });

  // Pledge Settings State
  const [pledgeSettings, setPledgeSettings] = useState([]);
  const [loadingPledge, setLoadingPledge] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [settingToDelete, setSettingToDelete] = useState(null);
  const [editingSetting, setEditingSetting] = useState(null);
  const [createForm, setCreateForm] = useState({
    name: '',
    interestRate: ''
  });
  const [creatingPledge, setCreatingPledge] = useState(false);
  const creatingPledgeRef = useRef(false);
  const [editForm, setEditForm] = useState({
    interestRate: ''
  });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  
  // Use ref to prevent duplicate API calls
  const loadingRef = useRef(false);
  const loanLoadingRef = useRef(false);

  // Today's Rates State
  const [todayRates, setTodayRates] = useState([]);
  const [loadingRates, setLoadingRates] = useState(false);
  const [showRateCreateModal, setShowRateCreateModal] = useState(false);
  const [showRateEditModal, setShowRateEditModal] = useState(false);
  const [showRateDeleteModal, setShowRateDeleteModal] = useState(false);
  const [rateToDelete, setRateToDelete] = useState(null);
  const [editingRate, setEditingRate] = useState(null);
  const [rateCreateForm, setRateCreateForm] = useState({
    name: '24K_GOLD',
    amount: ''
  });
  const [rateEditForm, setRateEditForm] = useState({
    amount: ''
  });
  const ratesLoadingRef = useRef(false);

  const handleNotificationChange = (e) => {
    const { name, value } = e.target;
    setNotificationTemplates(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = () => {
    // Handle save logic
    console.log('Saving settings:', { loanSettings, notificationTemplates });
  };

  const tabs = [
    { id: 'todayRates', label: t('settings.todayRates') },
    { id: 'loan', label: t('settings.loanSettings') },
    { id: 'pledge', label: t('settings.pledgeSettings') },
    { id: 'notifications', label: t('settings.notifications') },
    { id: 'language', label: t('settings.language') },
    { id: 'appearance', label: t('settings.appearance') || 'Appearance' }
  ];

  const metalOptions = ['Gold', 'Silver'];
  const rateMetalOptions = [
    { value: '24K_GOLD', label: t('settings.gold24K') },
    { value: 'SILVER', label: t('settings.goldSilver') }
  ];

  // Load loan settings
  const loadLoanSettings = useCallback(async () => {
    if (loanLoadingRef.current) return;
    
    loanLoadingRef.current = true;
    setLoadingLoan(true);
    try {
      const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
      const businessId = selectedBusiness?.id;

      if (!businessId) {
        setLoadingLoan(false);
        loanLoadingRef.current = false;
        return;
      }

      const result = await getSettingsByType(businessId, 'LOAN');
      if (result.success && result.data) {
        setLoanSettings(Array.isArray(result.data) ? result.data : []);
      } else {
        setToast({ show: true, message: result.message || 'Failed to load loan settings', type: 'error' });
      }
    } catch (error) {
      console.error('Error loading loan settings:', error);
      setToast({ show: true, message: 'Failed to load loan settings', type: 'error' });
    } finally {
      setLoadingLoan(false);
      loanLoadingRef.current = false;
    }
  }, []);

  // Load today's rates
  const loadTodayRates = useCallback(async () => {
    if (ratesLoadingRef.current) return;
    
    ratesLoadingRef.current = true;
    setLoadingRates(true);
    try {
      const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
      const businessId = selectedBusiness?.id;

      if (!businessId) {
        setLoadingRates(false);
        ratesLoadingRef.current = false;
        return;
      }

      const result = await getSettingsByType(businessId, 'TODAY_VALUE');
      if (result.success && result.data) {
        setTodayRates(Array.isArray(result.data) ? result.data : []);
      } else {
        setToast({ show: true, message: result.message || 'Failed to load rates', type: 'error' });
      }
    } catch (error) {
      console.error('Error loading rates:', error);
      setToast({ show: true, message: 'Failed to load rates', type: 'error' });
    } finally {
      setLoadingRates(false);
      ratesLoadingRef.current = false;
    }
  }, []);

  // Load pledge settings
  const loadPledgeSettings = useCallback(async () => {
    if (loadingRef.current) return;
    
    loadingRef.current = true;
    setLoadingPledge(true);
    try {
      const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
      const businessId = selectedBusiness?.id;

      if (!businessId) {
        setLoadingPledge(false);
        loadingRef.current = false;
        return;
      }

      const result = await getSettingsByType(businessId, 'PLEDGE');
      if (result.success && result.data) {
        setPledgeSettings(Array.isArray(result.data) ? result.data : []);
      } else {
        setToast({ show: true, message: result.message || 'Failed to load pledge settings', type: 'error' });
      }
    } catch (error) {
      console.error('Error loading pledge settings:', error);
      setToast({ show: true, message: 'Failed to load pledge settings', type: 'error' });
    } finally {
      setLoadingPledge(false);
      loadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'loan') {
      loadLoanSettings();
    } else if (activeTab === 'pledge') {
      loadPledgeSettings();
    } else if (activeTab === 'todayRates') {
      loadTodayRates();
    }
  }, [activeTab, loadLoanSettings, loadPledgeSettings, loadTodayRates]);

  // Handle loan settings
  const handleCreateLoanSetting = async (e) => {
    e.preventDefault();
    
    if (!loanCreateForm.name || !loanCreateForm.interestRate) {
      setToast({ show: true, message: 'Please fill name and interest rate', type: 'error' });
      return;
    }

    try {
      const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
      const businessId = selectedBusiness?.id;

      if (!businessId) {
        setToast({ show: true, message: 'No business selected', type: 'error' });
        return;
      }

      // Format amount as range string if both min and max are provided
      let amountString = '';
      if (loanCreateForm.amountMin && loanCreateForm.amountMax) {
        amountString = `${loanCreateForm.amountMin} - ${loanCreateForm.amountMax}`;
      } else if (loanCreateForm.amountMin) {
        amountString = loanCreateForm.amountMin;
      } else if (loanCreateForm.amountMax) {
        amountString = loanCreateForm.amountMax;
      }

      const settingData = {
        businessId,
        type: 'LOAN',
        name: loanCreateForm.name,
        interestRate: parseFloat(loanCreateForm.interestRate),
        ...(amountString ? { amount: amountString } : {})
      };

      const result = await createSetting(businessId, settingData);

      if (result.success) {
        setToast({ show: true, message: 'Loan setting created successfully', type: 'success' });
        setShowLoanCreateModal(false);
        setLoanCreateForm({ name: 'Gold', interestRate: '', amountMin: '', amountMax: '' });
        loadLoanSettings();
      } else {
        setToast({ show: true, message: result.message || 'Failed to create loan setting', type: 'error' });
      }
    } catch (error) {
      console.error('Error creating loan setting:', error);
      setToast({ show: true, message: 'Failed to create loan setting', type: 'error' });
    }
  };

  const handleEditLoanClick = (setting) => {
    setEditingLoanSetting(setting);
    
    // Parse amount range if it exists (format: "1000 - 10000")
    let amountMin = '';
    let amountMax = '';
    if (setting.amount) {
      const amountParts = setting.amount.split(' - ');
      if (amountParts.length === 2) {
        amountMin = amountParts[0].trim();
        amountMax = amountParts[1].trim();
      } else {
        // If it's just a single value, treat it as min
        amountMin = setting.amount.trim();
      }
    }
    
    setLoanEditForm({ 
      interestRate: setting.interestRate.toString(),
      amountMin: amountMin,
      amountMax: amountMax
    });
    setShowLoanEditModal(true);
  };

  const handleUpdateLoanSetting = async (e) => {
    e.preventDefault();

    if (!loanEditForm.interestRate) {
      setToast({ show: true, message: 'Please enter interest rate', type: 'error' });
      return;
    }

    try {
      const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
      const businessId = selectedBusiness?.id;

      if (!businessId || !editingLoanSetting) {
        setToast({ show: true, message: 'Missing information', type: 'error' });
        return;
      }

      // Format amount as range string if both min and max are provided
      let amountString = '';
      if (loanEditForm.amountMin && loanEditForm.amountMax) {
        amountString = `${loanEditForm.amountMin} - ${loanEditForm.amountMax}`;
      } else if (loanEditForm.amountMin) {
        amountString = loanEditForm.amountMin;
      } else if (loanEditForm.amountMax) {
        amountString = loanEditForm.amountMax;
      }

      const settingData = {
        businessId,
        type: 'LOAN',
        name: editingLoanSetting.name,
        interestRate: parseFloat(loanEditForm.interestRate),
        ...(amountString ? { amount: amountString } : {})
      };

      const result = await updateSetting(businessId, editingLoanSetting.id, settingData);

      if (result.success) {
        setToast({ show: true, message: 'Loan setting updated successfully', type: 'success' });
        setShowLoanEditModal(false);
        setEditingLoanSetting(null);
        setLoanEditForm({ interestRate: '', amountMin: '', amountMax: '' });
        loadLoanSettings();
      } else {
        setToast({ show: true, message: result.message || 'Failed to update loan setting', type: 'error' });
      }
    } catch (error) {
      console.error('Error updating loan setting:', error);
      setToast({ show: true, message: 'Failed to update loan setting', type: 'error' });
    }
  };

  const handleDeleteLoanClick = (setting) => {
    setLoanSettingToDelete(setting);
    setShowLoanDeleteModal(true);
  };

  const handleDeleteLoanConfirm = async () => {
    if (!loanSettingToDelete) return;

    try {
      const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
      const businessId = selectedBusiness?.id;

      if (!businessId) {
        setToast({ show: true, message: 'No business selected', type: 'error' });
        return;
      }

      const result = await deleteSetting(businessId, loanSettingToDelete.id);

      if (result.success) {
        setToast({ show: true, message: 'Loan setting deleted successfully', type: 'success' });
        setShowLoanDeleteModal(false);
        setLoanSettingToDelete(null);
        loadLoanSettings();
      } else {
        setToast({ show: true, message: result.message || 'Failed to delete loan setting', type: 'error' });
      }
    } catch (error) {
      console.error('Error deleting loan setting:', error);
      setToast({ show: true, message: 'Failed to delete loan setting', type: 'error' });
    }
  };

  // Handle pledge settings
  const handleCreatePledge = async (e) => {
    e.preventDefault();

    if (creatingPledgeRef.current) {
      return;
    }

    if (!createForm.name || !createForm.interestRate) {
      setToast({ show: true, message: 'Please fill name and interest rate', type: 'error' });
      return;
    }

    try {
      const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
      const businessId = selectedBusiness?.id;

      if (!businessId) {
        setToast({ show: true, message: 'No business selected', type: 'error' });
        return;
      }

      creatingPledgeRef.current = true;
      setCreatingPledge(true);

      const settingData = {
        businessId,
        type: 'PLEDGE',
        name: createForm.name,
        interestRate: parseFloat(createForm.interestRate)
      };

      const result = await createSetting(businessId, settingData);

      if (result.success) {
        setToast({ show: true, message: 'Pledge setting created successfully', type: 'success' });
        setShowCreateModal(false);
        setCreateForm({ name: '', interestRate: '' });
        loadPledgeSettings();
      } else {
        setToast({ show: true, message: result.message || 'Failed to create pledge setting', type: 'error' });
      }
    } catch (error) {
      console.error('Error creating pledge setting:', error);
      setToast({ show: true, message: 'Failed to create pledge setting', type: 'error' });
    } finally {
      creatingPledgeRef.current = false;
      setCreatingPledge(false);
    }
  };

  const handleEditClick = (setting) => {
    setEditingSetting(setting);
    setEditForm({ interestRate: setting.interestRate.toString() });
    setShowEditModal(true);
  };

  const handleUpdatePledge = async (e) => {
    e.preventDefault();

    if (!editForm.interestRate) {
      setToast({ show: true, message: 'Please enter interest rate', type: 'error' });
      return;
    }

    try {
      const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
      const businessId = selectedBusiness?.id;

      if (!businessId || !editingSetting) {
        setToast({ show: true, message: 'Missing information', type: 'error' });
        return;
      }

      const settingData = {
        businessId,
        type: 'PLEDGE',
        name: editingSetting.name,
        interestRate: parseFloat(editForm.interestRate)
      };

      const result = await updateSetting(businessId, editingSetting.id, settingData);

      if (result.success) {
        setToast({ show: true, message: 'Pledge setting updated successfully', type: 'success' });
        setShowEditModal(false);
        setEditingSetting(null);
        setEditForm({ interestRate: '' });
        loadPledgeSettings();
      } else {
        setToast({ show: true, message: result.message || 'Failed to update pledge setting', type: 'error' });
      }
    } catch (error) {
      console.error('Error updating pledge setting:', error);
      setToast({ show: true, message: 'Failed to update pledge setting', type: 'error' });
    }
  };

  const handleDeleteClick = (setting) => {
    setSettingToDelete(setting);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!settingToDelete) return;

    try {
      const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
      const businessId = selectedBusiness?.id;

      if (!businessId) {
        setToast({ show: true, message: 'No business selected', type: 'error' });
        return;
      }

      const result = await deleteSetting(businessId, settingToDelete.id);

      if (result.success) {
        setToast({ show: true, message: 'Pledge setting deleted successfully', type: 'success' });
        setShowDeleteModal(false);
        setSettingToDelete(null);
        loadPledgeSettings();
      } else {
        setToast({ show: true, message: result.message || 'Failed to delete pledge setting', type: 'error' });
      }
    } catch (error) {
      console.error('Error deleting pledge setting:', error);
      setToast({ show: true, message: 'Failed to delete pledge setting', type: 'error' });
    }
  };

  // Handle today's rates CRUD operations
  const handleCreateRate = async (e) => {
    e.preventDefault();
    
    if (!rateCreateForm.name || !rateCreateForm.amount) {
      setToast({ show: true, message: 'Please fill metal type and value', type: 'error' });
      return;
    }

    try {
      const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
      const businessId = selectedBusiness?.id;

      if (!businessId) {
        setToast({ show: true, message: 'No business selected', type: 'error' });
        return;
      }

      const settingData = {
        businessId,
        type: 'TODAY_VALUE',
        name: rateCreateForm.name,
        amount: rateCreateForm.amount
      };

      const result = await createSetting(businessId, settingData);

      if (result.success) {
        setToast({ show: true, message: t('settings.rateCreatedSuccess'), type: 'success' });
        setShowRateCreateModal(false);
        setRateCreateForm({ name: '24K_GOLD', amount: '' });
        loadTodayRates();
      } else {
        setToast({ show: true, message: result.message || 'Failed to create rate', type: 'error' });
      }
    } catch (error) {
      console.error('Error creating rate:', error);
      setToast({ show: true, message: 'Failed to create rate', type: 'error' });
    }
  };

  const handleEditRateClick = (rate) => {
    setEditingRate(rate);
    setRateEditForm({ amount: rate.amount || '' });
    setShowRateEditModal(true);
  };

  const handleUpdateRate = async (e) => {
    e.preventDefault();

    if (!rateEditForm.amount) {
      setToast({ show: true, message: 'Please enter rate value', type: 'error' });
      return;
    }

    try {
      const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
      const businessId = selectedBusiness?.id;

      if (!businessId || !editingRate) {
        setToast({ show: true, message: 'Missing information', type: 'error' });
        return;
      }

      const settingData = {
        businessId,
        type: 'TODAY_VALUE',
        name: editingRate.name,
        amount: rateEditForm.amount
      };

      const result = await updateSetting(businessId, editingRate.id, settingData);

      if (result.success) {
        setToast({ show: true, message: t('settings.rateUpdatedSuccess'), type: 'success' });
        setShowRateEditModal(false);
        setEditingRate(null);
        setRateEditForm({ amount: '' });
        loadTodayRates();
      } else {
        setToast({ show: true, message: result.message || 'Failed to update rate', type: 'error' });
      }
    } catch (error) {
      console.error('Error updating rate:', error);
      setToast({ show: true, message: 'Failed to update rate', type: 'error' });
    }
  };

  const handleDeleteRateClick = (rate) => {
    setRateToDelete(rate);
    setShowRateDeleteModal(true);
  };

  const handleDeleteRateConfirm = async () => {
    if (!rateToDelete) return;

    try {
      const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
      const businessId = selectedBusiness?.id;

      if (!businessId) {
        setToast({ show: true, message: 'No business selected', type: 'error' });
        return;
      }

      const result = await deleteSetting(businessId, rateToDelete.id);

      if (result.success) {
        setToast({ show: true, message: t('settings.rateDeletedSuccess'), type: 'success' });
        setShowRateDeleteModal(false);
        setRateToDelete(null);
        loadTodayRates();
      } else {
        setToast({ show: true, message: result.message || 'Failed to delete rate', type: 'error' });
      }
    } catch (error) {
      console.error('Error deleting rate:', error);
      setToast({ show: true, message: 'Failed to delete rate', type: 'error' });
    }
  };

  // Calculate rates based on 24K gold value
  const calculateDisplayRates = () => {
    const gold24K = todayRates.find(rate => rate.name === '24K_GOLD');
    const silver = todayRates.find(rate => rate.name === 'SILVER');
    
    const rates = [];
    
    if (gold24K) {
      const gold24KValue = parseFloat(gold24K.amount);
      rates.push({
        name: '24K Gold',
        value: gold24KValue,
        isEditable: true,
        id: gold24K.id,
        isCalculated: false
      });
      rates.push({
        name: '22K Gold',
        value: (gold24KValue * 22 / 24).toFixed(2),
        isEditable: false,
        isCalculated: true
      });
      rates.push({
        name: '18K Gold',
        value: (gold24KValue * 18 / 24).toFixed(2),
        isEditable: false,
        isCalculated: true
      });
    }
    
    if (silver) {
      rates.push({
        name: 'Silver',
        value: parseFloat(silver.amount),
        isEditable: true,
        id: silver.id,
        isCalculated: false
      });
    }
    
    return rates;
  };

  // Handle language change with API sync
  const handleLanguageChange = async (newLanguage) => {
    try {
      const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
      const businessId = selectedBusiness?.id;

      if (!businessId) {
        // Just change language locally if no business
        changeLanguage(newLanguage);
        const successMessage = newLanguage === 'hi' ? 'भाषा सफलतापूर्वक अपडेट की गई' : 
                              newLanguage === 'rj' ? 'भासा सफलतापूर्वक बदली गई' : 
                              'Language updated successfully';
        setToast({ 
          show: true, 
          message: successMessage, 
          type: 'success' 
        });
        return;
      }

      // Convert language code to 2-letter uppercase code for backend
      // 'en' -> 'EN', 'hi' -> 'HI'
      const backendLanguageCode = newLanguage.toUpperCase();

      // Update language in backend
      const settingData = {
        businessId,
        type: 'LANGUAGE',
        name: backendLanguageCode // 'EN' or 'HI'
      };

      // Check if language setting exists
      const existingSettings = await getSettingsByType(businessId, 'LANGUAGE');
      
      if (existingSettings.success && existingSettings.data && existingSettings.data.length > 0) {
        // Update existing language setting
        const languageSetting = existingSettings.data[0];
        await updateSetting(businessId, languageSetting.id, settingData);
      } else {
        // Create new language setting
        await createSetting(businessId, settingData);
      }

      // Change language locally
      changeLanguage(newLanguage);
      const successMessage = newLanguage === 'hi' ? 'भाषा सफलतापूर्वक अपडेट की गई' : 
                            newLanguage === 'rj' ? 'भासा सफलतापूर्वक बदली गई' : 
                            'Language updated successfully';
      setToast({ 
        show: true, 
        message: successMessage, 
        type: 'success' 
      });
    } catch (error) {
      console.error('Error updating language:', error);
      // Still change language locally even if API fails
      changeLanguage(newLanguage);
      const successMessage = newLanguage === 'hi' ? 'भाषा सफलतापूर्वक अपडेट की गई' : 
                            newLanguage === 'rj' ? 'भासा सफलतापूर्वक बदली गई' : 
                            'Language updated successfully';
      setToast({ 
        show: true, 
        message: successMessage, 
        type: 'success' 
      });
    }
  };

  return (
    <div>
      <div className="card">
        {/* Tabs */}
        <div className="tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Today's Rates Tab */}
        {activeTab === 'todayRates' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                {t('settings.todayRatesTitle')}
              </h3>
              <button
                className="btn btn-primary"
                onClick={() => setShowRateCreateModal(true)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <Plus size={16} />
                {t('settings.createTodayRate')}
              </button>
            </div>

            {loadingRates ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
                {t('settings.loadingSettings')}
              </div>
            ) : calculateDisplayRates().length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
                {t('settings.noRatesFound')}. {t('settings.clickToAdd')}
              </div>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('settings.goldType')}</th>
                      <th>{t('settings.todayValue')}</th>
                      <th>{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calculateDisplayRates().map((rate, index) => (
                      <tr key={rate.id || index}>
                        <td style={{ fontWeight: '500' }}>
                          {rate.name}
                          {rate.isCalculated && (
                            <span style={{ 
                              marginLeft: '0.5rem', 
                              fontSize: '0.75rem', 
                              color: 'var(--text-secondary)',
                              fontWeight: 'normal' 
                            }}>
                              ({t('settings.calculatedFrom24K')})
                            </span>
                          )}
                        </td>
                        <td>₹{Number(rate.value).toLocaleString('en-IN')}</td>
                        <td>
                          {rate.isEditable ? (
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                              <button
                                className="btn btn-outline"
                                onClick={() => handleEditRateClick(todayRates.find(r => r.id === rate.id))}
                                style={{
                                  padding: '0.375rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  minWidth: '32px',
                                  height: '32px',
                                  color: '#2563eb',
                                  borderColor: '#2563eb'
                                }}
                                title={t('settings.editRate')}
                              >
                                <Edit size={16} />
                              </button>
                              <button
                                className="btn btn-outline"
                                onClick={() => handleDeleteRateClick(todayRates.find(r => r.id === rate.id))}
                                style={{
                                  padding: '0.375rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  minWidth: '32px',
                                  height: '32px',
                                  color: '#ef4444',
                                  borderColor: '#ef4444'
                                }}
                                title={t('settings.deleteRate')}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          ) : (
                            <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Loan Settings Tab */}
        {activeTab === 'loan' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                {t('settings.loanSettings')}
              </h3>
              <button
                className="btn btn-primary"
                onClick={() => setShowLoanCreateModal(true)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <Plus size={16} />
                {t('settings.createLoanSetting')}
              </button>
            </div>

            {/* Filter and Search */}
            {!loadingLoan && loanSettings.length > 0 && (
              <div style={{ 
                display: 'flex', 
                gap: '1rem', 
                marginBottom: '1.5rem',
                flexWrap: 'wrap',
                alignItems: 'center'
              }}>
                {/* Filter by Name */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: '0' }}>
                  <Filter size={18} color="var(--text-secondary)" />
                  <select
                    value={loanFilter}
                    onChange={(e) => setLoanFilter(e.target.value)}
                    style={{
                      padding: '0.5rem',
                      borderRadius: '0.5rem',
                      border: '1px solid var(--border-color)',
                      fontSize: '0.875rem',
                      flex: 1,
                      backgroundColor: 'var(--bg-surface)',
                      color: 'var(--text-primary)',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="all">{t('settings.allMetals')}</option>
                    {metalOptions.map(option => (
                      <option key={option} value={option}>{option === 'Gold' ? t('settings.gold') : t('settings.silver')}</option>
                    ))}
                  </select>
                </div>

                {/* Search Box */}
                <div className="list-filter-search" style={{ flex: 1, minWidth: 0 }}>
                  <div className="list-filter-search-control">
                    <Search size={18} className="list-filter-search-icon" />
                    <input
                      type="text"
                      value={loanSearchTerm}
                      onChange={(e) => setLoanSearchTerm(e.target.value)}
                      placeholder={t('settings.searchPlaceholder')}
                      className={`search-input${loanSearchTerm.trim().length > 0 && loanSearchTerm.trim().length < 2 ? ' search-input--error' : ''}`}
                    />
                    {loanSearchTerm && (
                      <button
                        type="button"
                        className="list-filter-search-clear"
                        onClick={() => setLoanSearchTerm('')}
                        aria-label="Clear search"
                        title="Clear"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  {loanSearchTerm.trim().length > 0 && loanSearchTerm.trim().length < 2 && (
                    <p className="list-filter-search-error">
                      {t('loans.messages.searchMinChars')}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Filtered and Searched Settings */}
            {(() => {
              // Filter and search logic
              let filteredSettings = loanSettings;

              // Apply name filter
              if (loanFilter !== 'all') {
                filteredSettings = filteredSettings.filter(setting => setting.name === loanFilter);
              }

              // Apply search (min 2 characters)
              const loanSearchQuery = loanSearchTerm.trim();
              if (loanSearchQuery.length >= 2) {
                filteredSettings = filteredSettings.filter(setting => {
                  const q = loanSearchQuery.toLowerCase();
                  return (
                    String(setting.name || '').toLowerCase().includes(q) ||
                    String(setting.interestRate ?? '').toLowerCase().includes(q) ||
                    String(setting.amountMin ?? '').includes(q) ||
                    String(setting.amountMax ?? '').includes(q) ||
                    String(setting.amount ?? '').toLowerCase().includes(q)
                  );
                });
              }

              return loadingLoan ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
                  {t('settings.loadingSettings')}
                </div>
              ) : loanSettings.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
                  {t('settings.noSettingsFound')}. {t('settings.clickToAdd')}
                </div>
              ) : filteredSettings.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
                  {t('settings.noMatchingSettings')}
                </div>
              ) : (
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>{t('settings.metal')}</th>
                        <th>{t('settings.interestRate')}</th>
                        <th>{t('settings.amount')}</th>
                        <th>{t('common.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSettings.map((setting) => (
                      <tr key={setting.id}>
                        <td style={{ fontWeight: '500' }}>{setting.name}</td>
                        <td>{setting.interestRate}%</td>
                        <td>{setting.amount || '-'}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <button
                              className="btn btn-outline"
                              onClick={() => handleEditLoanClick(setting)}
                              style={{
                                padding: '0.375rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '32px',
                                height: '32px',
                                color: '#2563eb',
                                borderColor: '#2563eb'
                              }}
                              title="Edit interest rate"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              className="btn btn-outline"
                              onClick={() => handleDeleteLoanClick(setting)}
                              style={{
                                padding: '0.375rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '32px',
                                height: '32px',
                                color: '#ef4444',
                                borderColor: '#ef4444'
                              }}
                              title="Delete setting"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              );
            })()}
          </div>
        )}

        {/* Pledge Setting Tab */}
        {activeTab === 'pledge' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                {t('settings.pledgeSettings')}
              </h3>
              <button
                className="btn btn-primary"
                onClick={() => setShowCreateModal(true)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <Plus size={16} />
                {t('settings.createPledgeSetting')}
              </button>
            </div>

            {loadingPledge ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
                {t('settings.loadingSettings')}
              </div>
            ) : pledgeSettings.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
                {t('settings.noSettingsFound')}. {t('settings.clickToAdd')}
              </div>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('settings.name')}</th>
                      <th>{t('settings.interestRate')}</th>
                      <th>{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pledgeSettings.map((setting) => (
                      <tr key={setting.id}>
                        <td style={{ fontWeight: '500' }}>{setting.name}</td>
                        <td>{setting.interestRate}%</td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <button
                              className="btn btn-outline"
                              onClick={() => handleEditClick(setting)}
                              style={{
                                padding: '0.375rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '32px',
                                height: '32px',
                                color: '#2563eb',
                                borderColor: '#2563eb'
                              }}
                              title="Edit interest rate"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              className="btn btn-outline"
                              onClick={() => handleDeleteClick(setting)}
                              style={{
                                padding: '0.375rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '32px',
                                height: '32px',
                                color: '#ef4444',
                                borderColor: '#ef4444'
                              }}
                              title="Delete setting"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--text-primary)' }}>
              {t('settings.smsWhatsappTemplates')}
            </h3>
            
            <div className="form-group">
              <label className="form-label">{t('settings.loanExpiryReminder')}</label>
              <textarea
                name="loanExpiryReminder"
                className="form-textarea"
                value={notificationTemplates.loanExpiryReminder}
                onChange={handleNotificationChange}
                rows={4}
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('settings.paymentConfirmation')}</label>
              <textarea
                name="paymentConfirmation"
                className="form-textarea"
                value={notificationTemplates.paymentConfirmation}
                onChange={handleNotificationChange}
                rows={4}
              />
            </div>
          </div>
        )}

        {/* Language Tab */}
        {activeTab === 'language' && (
          <div>
            <h3 className="settings-lang-title">
              {t('settings.selectLanguage')}
            </h3>
            <p className="settings-lang-desc">
              {t('settings.languageDescription')}
            </p>

            <div className="settings-lang-grid">
              {[
                { code: 'en', title: 'English', subtitle: 'Switch to English' },
                { code: 'hi', title: 'हिंदी', subtitle: 'हिंदी में बदलें' },
                { code: 'rj', title: 'मारवाड़ी', subtitle: 'मारवाड़ी में बदलो' }
              ].map((opt) => {
                const selected = language === opt.code;
                return (
                  <button
                    key={opt.code}
                    type="button"
                    className={`settings-lang-card${selected ? ' is-selected' : ''}`}
                    onClick={() => handleLanguageChange(opt.code)}
                  >
                    <span className="settings-lang-icon">
                      <Globe size={24} />
                    </span>
                    <span className="settings-lang-text">
                      <span className="settings-lang-name">{opt.title}</span>
                      <span className="settings-lang-sub">{opt.subtitle}</span>
                    </span>
                    {selected && <span className="settings-lang-check">✓</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Appearance Tab */}
        {activeTab === 'appearance' && (
          <div>
            <h3 className="settings-lang-title">
              {t('settings.selectTheme') || 'Select Theme'}
            </h3>
            <p className="settings-lang-desc">
              {t('settings.themeDescription') || 'Choose light or dark mode for the application'}
            </p>

            <div className="settings-lang-grid">
              {[
                {
                  code: 'light',
                  title: t('common.switchToLight') || 'Light mode',
                  subtitle: t('settings.lightThemeHint') || 'Bright background for daytime use',
                  icon: Sun
                },
                {
                  code: 'dark',
                  title: t('common.switchToDark') || 'Dark mode',
                  subtitle: t('settings.darkThemeHint') || 'Dark background for low light',
                  icon: Moon
                }
              ].map((opt) => {
                const selected = theme === opt.code;
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.code}
                    type="button"
                    className={`settings-lang-card${selected ? ' is-selected' : ''}`}
                    onClick={() => setTheme(opt.code)}
                  >
                    <span className="settings-lang-icon">
                      <Icon size={24} />
                    </span>
                    <span className="settings-lang-text">
                      <span className="settings-lang-name">{opt.title}</span>
                      <span className="settings-lang-sub">{opt.subtitle}</span>
                    </span>
                    {selected && <span className="settings-lang-check">✓</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Save Button - Only show for notifications tab */}
        {activeTab === 'notifications' && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2rem' }}>
            <button className="btn btn-primary" onClick={handleSave}>
              <Save size={16} />
              {t('common.saveChanges')}
            </button>
          </div>
        )}
      </div>

      {/* Toast */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={4000}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}

      {/* Create Loan Setting Modal */}
      {showLoanCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--bg-surface)',
            padding: '2rem',
            borderRadius: '8px',
            minWidth: '0', width: 'min(400px, 95vw)',
            maxWidth: '90vw',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1.25rem', fontWeight: '600' }}>
                {t('settings.createLoanInterestSetting')}
              </h3>
              <button
                onClick={() => {
                  setShowLoanCreateModal(false);
                  setLoanCreateForm({ name: 'Gold', interestRate: '', amountMin: '', amountMax: '' });
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  color: 'var(--text-secondary)'
                }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleCreateLoanSetting}>
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label className="form-label">{t('settings.metal')}</label>
                <select
                  className="form-input"
                  value={loanCreateForm.name}
                  onChange={(e) => setLoanCreateForm({ ...loanCreateForm, name: e.target.value })}
                  required
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem' }}
                >
                  {metalOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label className="form-label">{t('settings.interestRate')}</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-input"
                  value={loanCreateForm.interestRate}
                  onChange={(e) => setLoanCreateForm({ ...loanCreateForm, interestRate: e.target.value })}
                  placeholder={t('settings.interestRatePlaceholder')}
                  required
                />
              </div>

              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label className="form-label">{t('settings.amountRange')}</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="number"
                    step="1"
                    className="form-input"
                    value={loanCreateForm.amountMin}
                    onChange={(e) => setLoanCreateForm({ ...loanCreateForm, amountMin: e.target.value })}
                    placeholder={t('settings.minPlaceholder')}
                    style={{ flex: 1 }}
                  />
                  <span style={{ color: 'var(--text-secondary)' }}>-</span>
                  <input
                    type="number"
                    step="1"
                    className="form-input"
                    value={loanCreateForm.amountMax}
                    onChange={(e) => setLoanCreateForm({ ...loanCreateForm, amountMax: e.target.value })}
                    placeholder={t('settings.maxPlaceholder')}
                    style={{ flex: 1 }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => {
                    setShowLoanCreateModal(false);
                    setLoanCreateForm({ name: 'Gold', interestRate: '', amountMin: '', amountMax: '' });
                  }}
                >
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary">
                  {t('settings.createSettingBtn')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Loan Setting Modal */}
      {showLoanEditModal && editingLoanSetting && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--bg-surface)',
            padding: '2rem',
            borderRadius: '8px',
            minWidth: '0', width: 'min(400px, 95vw)',
            maxWidth: '90vw',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1.25rem', fontWeight: '600' }}>
                {t('settings.editLoanSetting')}
              </h3>
              <button
                onClick={() => {
                  setShowLoanEditModal(false);
                  setEditingLoanSetting(null);
                  setLoanEditForm({ interestRate: '', amountMin: '', amountMax: '' });
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  color: 'var(--text-secondary)'
                }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleUpdateLoanSetting}>
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label className="form-label">{t('settings.metal')}</label>
                <input
                  type="text"
                  className="form-input"
                  value={editingLoanSetting.name}
                  disabled
                  style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label className="form-label">{t('settings.interestRate')}</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-input"
                  value={loanEditForm.interestRate}
                  onChange={(e) => setLoanEditForm({ ...loanEditForm, interestRate: e.target.value })}
                  placeholder={t('settings.interestRatePlaceholder')}
                  required
                />
              </div>

              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label className="form-label">{t('settings.amountRange')}</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="number"
                    step="1"
                    className="form-input"
                    value={loanEditForm.amountMin}
                    onChange={(e) => setLoanEditForm({ ...loanEditForm, amountMin: e.target.value })}
                    placeholder={t('settings.minPlaceholder')}
                    style={{ flex: 1 }}
                  />
                  <span style={{ color: 'var(--text-secondary)' }}>-</span>
                  <input
                    type="number"
                    step="1"
                    className="form-input"
                    value={loanEditForm.amountMax}
                    onChange={(e) => setLoanEditForm({ ...loanEditForm, amountMax: e.target.value })}
                    placeholder={t('settings.maxPlaceholder')}
                    style={{ flex: 1 }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => {
                    setShowLoanEditModal(false);
                    setEditingLoanSetting(null);
                    setLoanEditForm({ interestRate: '', amountMin: '', amountMax: '' });
                  }}
                >
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary">
                  {t('settings.updateBtn')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Loan Setting Confirmation Modal */}
      {showLoanDeleteModal && loanSettingToDelete && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--bg-surface)',
            padding: '2rem',
            borderRadius: '8px',
            minWidth: '0', width: 'min(400px, 95vw)',
            maxWidth: '90vw',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', color: 'var(--text-primary)' }}>{t('common.confirmDelete')}</h3>
            <p style={{ margin: '0 0 1.5rem 0', color: 'var(--text-secondary)' }}>
              {t('common.areYouSure')} <strong>{loanSettingToDelete.name}</strong>?
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-outline"
                onClick={() => {
                  setShowLoanDeleteModal(false);
                  setLoanSettingToDelete(null);
                }}
                style={{ padding: '0.5rem 1rem' }}
              >
                {t('common.cancel')}
              </button>
              <button
                className="btn"
                onClick={handleDeleteLoanConfirm}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none'
                }}
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Pledge Setting Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--bg-surface)',
            padding: '2rem',
            borderRadius: '8px',
            minWidth: '0', width: 'min(400px, 95vw)',
            maxWidth: '90vw',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1.25rem', fontWeight: '600' }}>
                {t('settings.createNewPledgeSetting')}
              </h3>
              <button
                type="button"
                disabled={creatingPledge}
                onClick={() => {
                  if (creatingPledge) return;
                  setShowCreateModal(false);
                  setCreateForm({ name: '', interestRate: '' });
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: creatingPledge ? 'not-allowed' : 'pointer',
                  padding: '0.25rem',
                  color: 'var(--text-secondary)',
                  opacity: creatingPledge ? 0.5 : 1
                }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleCreatePledge}>
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label className="form-label">{t('settings.name')}</label>
                <input
                  type="text"
                  className="form-input"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  placeholder={t('settings.enterName')}
                  required
                  disabled={creatingPledge}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label className="form-label">{t('settings.interestRate')}</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-input"
                  value={createForm.interestRate}
                  onChange={(e) => setCreateForm({ ...createForm, interestRate: e.target.value })}
                  placeholder={t('settings.interestRatePlaceholder')}
                  required
                  disabled={creatingPledge}
                />
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-outline"
                  disabled={creatingPledge}
                  onClick={() => {
                    if (creatingPledge) return;
                    setShowCreateModal(false);
                    setCreateForm({ name: '', interestRate: '' });
                  }}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={creatingPledge}
                  style={creatingPledge ? { opacity: 0.7, cursor: 'not-allowed' } : undefined}
                >
                  {creatingPledge
                    ? (t('settings.creatingSetting') || 'Creating...')
                    : t('settings.createSettingBtn')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Pledge Setting Modal */}
      {showEditModal && editingSetting && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--bg-surface)',
            padding: '2rem',
            borderRadius: '8px',
            minWidth: '0', width: 'min(400px, 95vw)',
            maxWidth: '90vw',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1.25rem', fontWeight: '600' }}>
                {t('settings.editInterestRate')}
              </h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingSetting(null);
                  setEditForm({ interestRate: '' });
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  color: 'var(--text-secondary)'
                }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleUpdatePledge}>
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label className="form-label">{t('settings.name')}</label>
                <input
                  type="text"
                  className="form-input"
                  value={editingSetting.name}
                  disabled
                  style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label className="form-label">{t('settings.interestRate')}</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-input"
                  value={editForm.interestRate}
                  onChange={(e) => setEditForm({ interestRate: e.target.value })}
                  placeholder={t('settings.interestRatePlaceholder')}
                  required
                />
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingSetting(null);
                    setEditForm({ interestRate: '' });
                  }}
                >
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary">
                  {t('settings.updateBtn')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && settingToDelete && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--bg-surface)',
            padding: '2rem',
            borderRadius: '8px',
            minWidth: '0', width: 'min(400px, 95vw)',
            maxWidth: '90vw',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', color: 'var(--text-primary)' }}>{t('common.confirmDelete')}</h3>
            <p style={{ margin: '0 0 1.5rem 0', color: 'var(--text-secondary)' }}>
              {t('common.areYouSure')} <strong>{settingToDelete.name}</strong>?
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-outline"
                onClick={() => {
                  setShowDeleteModal(false);
                  setSettingToDelete(null);
                }}
                style={{ padding: '0.5rem 1rem' }}
              >
                {t('common.cancel')}
              </button>
              <button
                className="btn"
                onClick={handleDeleteConfirm}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none'
                }}
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Rate Modal */}
      {showRateCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--bg-surface)',
            padding: '2rem',
            borderRadius: '8px',
            minWidth: '0', width: 'min(400px, 95vw)',
            maxWidth: '90vw',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1.25rem', fontWeight: '600' }}>
                {t('settings.createNewRate')}
              </h3>
              <button
                onClick={() => {
                  setShowRateCreateModal(false);
                  setRateCreateForm({ name: '24K_GOLD', amount: '' });
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  color: 'var(--text-secondary)'
                }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleCreateRate}>
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label className="form-label">{t('settings.goldType')}</label>
                <select
                  className="form-input"
                  value={rateCreateForm.name}
                  onChange={(e) => setRateCreateForm({ ...rateCreateForm, name: e.target.value })}
                  required
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem' }}
                >
                  {rateMetalOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label className="form-label">{t('settings.todayValue')}</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-input"
                  value={rateCreateForm.amount}
                  onChange={(e) => setRateCreateForm({ ...rateCreateForm, amount: e.target.value })}
                  placeholder="e.g., 75000"
                  required
                />
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => {
                    setShowRateCreateModal(false);
                    setRateCreateForm({ name: '24K_GOLD', amount: '' });
                  }}
                >
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary">
                  {t('settings.createRateBtn')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Rate Modal */}
      {showRateEditModal && editingRate && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--bg-surface)',
            padding: '2rem',
            borderRadius: '8px',
            minWidth: '0', width: 'min(400px, 95vw)',
            maxWidth: '90vw',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1.25rem', fontWeight: '600' }}>
                {t('settings.editRateTitle')}
              </h3>
              <button
                onClick={() => {
                  setShowRateEditModal(false);
                  setEditingRate(null);
                  setRateEditForm({ amount: '' });
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  color: 'var(--text-secondary)'
                }}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleUpdateRate}>
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label className="form-label">{t('settings.goldType')}</label>
                <input
                  type="text"
                  className="form-input"
                  value={rateMetalOptions.find(opt => opt.value === editingRate.name)?.label || editingRate.name}
                  disabled
                  style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label className="form-label">{t('settings.todayValue')}</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-input"
                  value={rateEditForm.amount}
                  onChange={(e) => setRateEditForm({ amount: e.target.value })}
                  placeholder="e.g., 75000"
                  required
                />
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => {
                    setShowRateEditModal(false);
                    setEditingRate(null);
                    setRateEditForm({ amount: '' });
                  }}
                >
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary">
                  {t('settings.updateBtn')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Rate Confirmation Modal */}
      {showRateDeleteModal && rateToDelete && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--bg-surface)',
            padding: '2rem',
            borderRadius: '8px',
            minWidth: '0', width: 'min(400px, 95vw)',
            maxWidth: '90vw',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', color: 'var(--text-primary)' }}>{t('common.confirmDelete')}</h3>
            <p style={{ margin: '0 0 1.5rem 0', color: 'var(--text-secondary)' }}>
              {t('common.areYouSure')} <strong>{rateMetalOptions.find(opt => opt.value === rateToDelete.name)?.label || rateToDelete.name}</strong>?
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-outline"
                onClick={() => {
                  setShowRateDeleteModal(false);
                  setRateToDelete(null);
                }}
                style={{ padding: '0.5rem 1rem' }}
              >
                {t('common.cancel')}
              </button>
              <button
                className="btn"
                onClick={handleDeleteRateConfirm}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none'
                }}
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
